/**
 * Suggested-billing-code transport contract (GDN-2614 / GDN-2613).
 *
 * The note shape stays generated — we import `NoteResponse` — but the two
 * GDN-2613 fields are declared locally, on purpose and temporarily. Codegen
 * (locally and in `.github/workflows/playwright-pr.yml`) points
 * `VITE_API_URL` at api-test, which runs deployed code that predates
 * GDN-2613, so the generated `NoteResponse` has neither field and there is no
 * generated `SuggestionStatus`. Importing them would fail typecheck on every
 * frontend CI run, not just this feature's, until the backend deploys.
 *
 * GDN-2616 deletes the local declarations and goes back to
 * `import type { NoteResponse, SuggestionStatus } from '@store/generated/notes'`
 * once GDN-2613 is live on api-test. The union below must stay in step with
 * `SuggestionStatus` in
 * `services/ehr_api/lib/modules/resources/notes/routes/schemas/note_schemas.py`.
 *
 * Every consumer goes through the two accessors — `readSuggestionStatus()` /
 * `readSuggestionExpectedBy()` — so the absent-field degradation contract
 * (see below) has exactly one home.
 */

import type { NoteResponse } from '@store/generated/notes';

/** TEMPORARY — mirrors the backend enum; delete in GDN-2616. */
export type SuggestionStatus = 'not_expected' | 'expected' | 'ready';

/** TEMPORARY — the fields api-test's spec doesn't carry yet; delete in GDN-2616. */
type NoteWithSuggestionFields = Partial<NoteResponse> & {
  suggestion_status?: SuggestionStatus | null;
  suggestion_expected_by?: string | null;
};

/**
 * Read the suggestion status off a note response.
 *
 * Returns `null` when the field is absent (older backend) OR carries a value
 * outside the contract. Both cases mean "this backend does not speak the
 * suggestion contract" and every new behaviour must degrade to the pre-GDN-2614
 * rendering — see `useSuggestionExpectation`.
 */
export function readSuggestionStatus(note: unknown): SuggestionStatus | null {
  const raw = (note as NoteWithSuggestionFields | null | undefined)?.suggestion_status;
  return raw === 'not_expected' || raw === 'expected' || raw === 'ready' ? raw : null;
}

/**
 * Read `suggestion_expected_by` as epoch milliseconds.
 *
 * Returns `null` when absent or unparseable — callers fall back to a
 * client-side window anchored on the first `expected` observation.
 */
export function readSuggestionExpectedBy(note: unknown): number | null {
  const raw = (note as NoteWithSuggestionFields | null | undefined)?.suggestion_expected_by;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? null : parsed;
}
